import { useCallback, useEffect, useRef, useState } from 'react';
import { cloudStorage } from '@tma.js/sdk-react';

import { storage, StorageKey } from '@/lib/storage';

interface CloudPrefKey {
  /** Telegram CloudStorage key — ONLY [A-Za-z0-9_-], dots are rejected. */
  cloudKey: string;
  /** localStorage mirror — repo convention `qlash.` prefix. */
  localKey: string;
}

/**
 * Cross-device preference: Telegram CloudStorage as source of truth with a
 * write-through localStorage cache (PRD-02 §4.2). localStorage answers
 * synchronously on mount (no flash for returning users on the same device);
 * CloudStorage hydration catches the cross-device case (seen on phone A,
 * reopened on phone B). Outside Telegram (standalone web / dev) it degrades
 * to plain localStorage.
 *
 * `ready` is false only while the FIRST cloud hydration is in flight AND the
 * local cache was empty — gate first-run UI on it to avoid flashing the
 * wrong branch.
 */
export function useCloudPref({ cloudKey, localKey }: CloudPrefKey) {
  const [value, setValue] = useState<string | null>(() => storage.get(localKey));
  const [ready, setReady] = useState<boolean>(() => storage.get(localKey) !== null);
  const hydrated = useRef(false);
  // A local set() during in-flight hydration must win over the older cloud value.
  const written = useRef(false);

  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;
    let supported = false;
    try {
      supported = cloudStorage.isSupported();
    } catch {
      supported = false;
    }
    if (!supported) {
      setReady(true);
      return;
    }
    void cloudStorage
      .getItem(cloudKey)
      .then((v: string) => {
        if (v && !written.current) {
          storage.set(localKey, v);
          setValue(v);
        }
        setReady(true);
      })
      .catch(() => setReady(true));
  }, [cloudKey, localKey]);

  const set = useCallback(
    (v: string) => {
      written.current = true;
      // Sync local write FIRST: even if the async cloud write fails the flag
      // sticks on this device (no wizard-reappears race).
      storage.set(localKey, v);
      setValue(v);
      try {
        if (cloudStorage.isSupported()) {
          void cloudStorage.setItem(cloudKey, v).catch(() => {
            /* best-effort — local cache already updated */
          });
        }
      } catch {
        /* unsupported env — local cache is enough */
      }
    },
    [cloudKey, localKey],
  );

  return { value, ready, set };
}

/** Registry of cloud-backed prefs (cloud keys must avoid dots). */
export const CloudPref = {
  OnboardingSeen: {
    cloudKey: 'qlash_onboarding_seen',
    localKey: StorageKey.OnboardingSeen,
  },
} as const satisfies Record<string, CloudPrefKey>;
